import {
  Edit,
  SimpleForm,
  useNotify,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Autocomplete, TextField, FormControl } from "@mui/material";

//Typescript
import CityResponse from "../../interfaces/City/CityResponse";
import AreaResponse from "../../interfaces/Area/AreaResponse";

//store
import { setCustomSelectOpen } from "../../store/reducers/customSelect";

//Icons
import { LocationIcon, PhoneIcon, EditIcon } from "../Svg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

//Components
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import Customers from "../Customers/Customer";
import LocalityList from "../Locality/LocalityList";
import AreaService from "../../services/AreaService";
import CityService from "../../services/CityService";

//Common styles
import {
  WorkspaceContainer,
  WorkspacePanel,
  WorkspaceHeader,
  WorkspaceHeaderMeta,
  HeaderMetaItem,
  HeaderMetaGroup,
  HeaderMetaLabel,
  WorkspaceSection,
  SectionHeader,
  WorkspaceTitle,
  WorkspaceShowButton,
  SectionTitle,
  WorkspaceSubtitle,
  SectionTitleRow,
  WorkSpaceTitleRow,
  WorkspaceIconBadge,
  FormGrid,
  FormFieldGroup,
  FormLabel,
  StyledTextInput,
  WorkspaceActionBar,
  PrimaryActionButton,
  StatusToggleSwitch,
  Paragraph,
  StyledInputButton,
} from "../../styles/commonStyles";

const RESOURCES = ["Customers", "Localities"];
const RESOURCES_LIST = [Customers, LocalityList];

const EditActions = () => null;
const EmptyToolbar = () => null;

const areaService = new AreaService();
const cityService = new CityService();

const HouseEditFormContent = () => {
  const record = useRecordContext();
  const { setValue } = useFormContext();
  const dispatch = useDispatch();

  const [selectedResource, setSelectedResource] = useState(1);
  const [localityName, setLocalityName] = useState(record?.locality);
  const [cityList, setCityList] = useState<CityResponse[]>([]);
  const [selectedCity, setSelectedCity] = useState<any>(null);

  const [areaList, setAreaList] = useState<AreaResponse[]>([]);
  const [selectedArea, setSelectedArea] = useState<any>(null);

  const isActive = record?.status === "ACTIVE" || Boolean(record?.isActive);

  const openPopup = (resourceIndex: number) => {
    setSelectedResource(resourceIndex);
    dispatch(setCustomSelectOpen({ open: true }));
  };

  useEffect(() => {
    if (record?.locality) {
      setLocalityName(record.localityName);
    }
  }, [record]);

  useEffect(() => {
    const debouncedFetchAreas = debounce(() => {
      const filters: Record<string, number | string> = {};

      if (record?.cityId) {
        filters.cityId = record.cityId;
      }

      areaService
        .fetchAllAreas(1000, 0, filters, { field: "name", order: "ASC" })
        .then((res) => {
          setAreaList(res?.data || []);
        });
    }, 500);
    debouncedFetchAreas();
    return () => {
      debouncedFetchAreas.cancel();
    };
  }, [record?.cityId]);

  useEffect(() => {
    if (record?.areaId && record?.area) {
      setSelectedArea({
        value: record.areaId,
        label: record.area,
      });
    }
  }, [record]);

  useEffect(() => {
    cityService
      .fetchAllCities(1000, 0, { isCurrentlyServing: "YES" }, {})
      .then((res) => {
        setCityList(res?.data);
      });
    areaService
      .fetchAllAreas(1000, 0, {}, { field: "name", order: "ASC" })
      .then((res) => {
        setAreaList(res?.data);
      });
  }, []);

  useEffect(() => {
    if (record?.cityId && record?.cityName) {
      setSelectedCity({
        value: record.cityId,
        label: record.cityName,
      });
    }
  }, [record]);

  const handleClose = (selected: any) => {
    if (!selected) return;

    setValue("localityId", selected.id, { shouldDirty: true });
    setValue("cityId", selected.cityId, { shouldDirty: true });
    setValue("areaId", selected.areaId, { shouldDirty: true });
    setValue("city", selected.cityName, { shouldDirty: true });
    setValue("area", selected.areaName, { shouldDirty: true });

    setLocalityName(selected.name);
  };

  const handleSaveButton = () => {};

  const notify = useNotify();
  const redirect = useRedirect();

  return (
    <>
      <ResourceSelectDialog
        name={RESOURCES[selectedResource]}
        list={RESOURCES_LIST[selectedResource]}
        onClose={handleClose}
      />

      <WorkspaceHeader>
        <SectionTitleRow>
          <WorkspaceIconBadge>
            <EditIcon />
          </WorkspaceIconBadge>
          <div>
            <WorkspaceTitle>House Edit</WorkspaceTitle>
            <WorkspaceSubtitle>Update house details</WorkspaceSubtitle>
          </div>
        </SectionTitleRow>

        <WorkspaceHeaderMeta>
          <HeaderMetaGroup>
            <HeaderMetaLabel>HSE ID</HeaderMetaLabel>
            <HeaderMetaItem>
              {record?.id?.toString().padStart(5, "0")}
            </HeaderMetaItem>
          </HeaderMetaGroup>

          <HeaderMetaGroup>
            <PhoneIcon />
            <HeaderMetaItem>{record?.contact || "—"}</HeaderMetaItem>
          </HeaderMetaGroup>

          <HeaderMetaGroup>
            <HeaderMetaLabel>Status</HeaderMetaLabel>
            <StatusToggleSwitch active={isActive}>
              <span className="slider" />
            </StatusToggleSwitch>
          </HeaderMetaGroup>
        </WorkspaceHeaderMeta>
      </WorkspaceHeader>

      <WorkspacePanel>
        <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <LocationIcon />
              <SectionTitle>Location Details</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
          <WorkspaceShowButton variant="outlined" label="Show" size="small" />
        </WorkspaceSection>

        <FormLabel>Full Address</FormLabel>
        <StyledTextInput
          variant="outlined"
          source="address"
          fullWidth
          label={false}
        />

        <FormGrid>
          <FormFieldGroup>
            <FormLabel>Landmark</FormLabel>
            <StyledTextInput
              source="locality"
              fullWidth
              variant="outlined"
              label={false}
            />
          </FormFieldGroup>

          <FormFieldGroup>
            <FormLabel>Locality</FormLabel>
            <StyledTextInput source="locality" style={{ display: "none" }} />

            <StyledInputButton
              variant="outlined"
              fullWidth
              onClick={() => openPopup(1)}
              sx={{
                justifyContent: "space-between",
                textTransform: "none",
                textAlign: "left",
                color: "text.primary",
                borderColor: "divider",
                display: "flex",

                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Paragraph>
                {localityName ? `${localityName}` : "Select Locality"}
              </Paragraph>
              <ArrowDropDownIcon />
            </StyledInputButton>
          </FormFieldGroup>
        </FormGrid>

        <FormGrid>
          <FormFieldGroup>
            <FormLabel>Area</FormLabel>
            <FormControl
              fullWidth
              sx={{
                padding: "0",
                margin: "0",
              }}
            >
              <Autocomplete
                id="area-select"
                options={areaList.map((area) => ({
                  value: area.id,
                  label: area.name,
                }))}
                value={selectedArea}
                getOptionLabel={(option) => option?.label ?? ""}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                onChange={(_, newValue) => {
                  setSelectedArea(newValue);
                  setValue("areaId", newValue?.value ?? null, {
                    shouldDirty: true,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      record.areaName ? record.areaName : `Select Area`
                    }
                    sx={{
                      "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiOutlinedInput-input":
                        {
                          paddingTop: "5px",
                          paddingBottom: "5px",
                        },
                    }}
                  />
                )}
              />
            </FormControl>
          </FormFieldGroup>
          <FormFieldGroup>
            <FormLabel>Map Location</FormLabel>
            <StyledTextInput
              variant="outlined"
              source="city"
              fullWidth
              label={false}
            />
          </FormFieldGroup>
          <FormFieldGroup>
            <FormLabel>City</FormLabel>
            <Autocomplete
              options={cityList.map((city) => ({
                value: city.id,
                label: city.name,
              }))}
              value={selectedCity}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_, newValue) => {
                setSelectedCity(newValue);

                setValue("cityId", newValue?.value ?? null, {
                  shouldDirty: true,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select City"
                  sx={{
                    "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiOutlinedInput-input":
                      {
                        paddingTop: "5px",
                        paddingBottom: "5px",
                      },
                  }}
                />
              )}
            />
          </FormFieldGroup>
        </FormGrid>

        <FormGrid>
          <FormFieldGroup>
            <FormLabel>Latitude</FormLabel>
            <StyledTextInput
              variant="outlined"
              source="lat"
              fullWidth
              label={false}
            />
          </FormFieldGroup>
          <FormFieldGroup>
            <FormLabel>Longitude</FormLabel>
            <StyledTextInput
              variant="outlined"
              source="long"
              fullWidth
              label={false}
            />
          </FormFieldGroup>
        </FormGrid>
      </WorkspacePanel>

      <WorkspaceActionBar>
        <PrimaryActionButton
          onClick={handleSaveButton}
          // @ts-ignore
          mutationMode="pessimistic"
          mutationOptions={{
            onSuccess: (response) => {
              notify("Element updated", { type: "success" });
              redirect("show");
            },

            onError: (error) => {
              // @ts-ignore
              notify(error, { type: "error" });
            },
          }}
        />
      </WorkspaceActionBar>
    </>
  );
};

const HouseEditForm = () => (
  <SimpleForm toolbar={<EmptyToolbar />}>
    <HouseEditFormContent />
  </SimpleForm>
);

const HouseEdit = (props: any) => {
  const transform = (data: any) => {
    return {
      address: data.address,
      locality: data.locality,
      city: data.city,
      lat: parseFloat(data.lat),
      long: parseFloat(data.long),
      localityId: Number(data.localityId),
      areaId: Number(data.areaId),
      cityId: Number(data.cityId),
    };
  };

  return (
    <WorkspaceContainer>
      <Edit
        {...props}
        transform={transform}
        actions={<EditActions />}
        mutationMode="pessimistic"
        redirect="show"
        sx={{
          "& .RaEdit-card": {
            boxShadow: "none",
            background: "transparent",
            "& .MuiCardContent-root": {
              padding: 0,
              "&:last-child": {
                paddingBottom: 0,
              },
            },
          },
        }}
      >
        <HouseEditForm />
      </Edit>
    </WorkspaceContainer>
  );
};

export default HouseEdit;
